// ContactChangeDialog — OTP-gated donor contact change (CHG-2942 / Slice C).
//
// Two-step modal over the donor-portal contact-change pair
// (specs/tier-05-experience/profile-contact-change.cross-screen.yaml,
// surface `portal-profile-phone-change`):
//
//   enter  → POST /my-giving/me/request-phone-change  (stage + send SMS OTP)
//   code   → POST /my-giving/me/confirm-phone-change  (verify, swap, revoke)
//   done   → success state; closing invalidates the profile query upstream.
//
// Composed entirely from the FormDialog primitive (fields resolve through
// @fields/registry — `phone` is the canonical phone primitive; its real
// country-hint implementation is owned by ε CHG-1161 and this dialog picks
// it up for free when it lands). No hand-rolled inputs.
//
// Structured kind-generically so the email variant (C2 follow-up — backend
// routes landed in CHG-2943/Slice D) is a config entry + two API helpers,
// not a new component.
//
// Error copy deliberately distinguishes rate-limit / expired-code /
// invalid-code / SMS-unavailable WITHOUT account-enumeration signals (no
// "this number belongs to another account" style messages — the backend
// doesn't leak that and neither do we).

import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FormDialog, type FormFieldDef } from "../composition/FormDialog";
import { requestForceLogout } from "../../api/client";
import {
  requestPhoneChange,
  confirmPhoneChange,
} from "../../api/me-profile";

export type ContactChangeKind = "phone";
// C2: widen to "phone" | "email" — the /my-giving/me/*email-change routes
// landed in CHG-2943; the UI wiring is the deliberate C2 follow-up.

type Step = "enter" | "code" | "done";

interface KindConfig {
  title: Record<Step, string>;
  enterField: FormFieldDef;
  enterDescription: string;
  submitLabel: Record<Step, string>;
  codeSentTo: (value: string) => string;
  successCopy: string;
  smsUnavailableCopy: string;
  /** Light client-side shape check; returns an error string or null. */
  validate: (value: string) => string | null;
  /** Canonicalize before sending (strip separators → E.164-ish). */
  normalize: (value: string) => string;
  request: (value: string) => Promise<unknown>;
  confirm: (code: string) => Promise<unknown>;
}

/** Strip the separators people type into phone numbers (spaces, dashes,
 *  dots, parens) so "+1 (555) 000-0000" submits as "+15550000000". */
function normalizePhone(raw: string): string {
  return raw.replace(/[\s\-().]/g, "");
}

const KIND_CONFIG: Record<ContactChangeKind, KindConfig> = {
  phone: {
    title: {
      enter: "Change phone number",
      code: "Verify new phone",
      done: "Phone number updated",
    },
    enterField: {
      name: "phone",
      type: "phone",
      label: "New phone number",
      required: true,
      helperText:
        "International format with country code, e.g. +1 555 000 0000.",
    },
    enterDescription:
      "Enter your new phone number. We'll text a 6-digit verification code to it.",
    submitLabel: { enter: "Send code", code: "Verify", done: "Done" },
    codeSentTo: (value) => `Enter the 6-digit code we texted to ${value}.`,
    successCopy:
      "Your phone number is verified and updated. For your security, other signed-in sessions were signed out.",
    smsUnavailableCopy:
      "We couldn't send the verification text right now. Please try again in a few minutes.",
    validate: (value) =>
      /^\+[1-9]\d{6,14}$/.test(normalizePhone(value))
        ? null
        : "Enter the number in international format, e.g. +1 555 000 0000.",
    normalize: normalizePhone,
    request: (value) => requestPhoneChange(value),
    confirm: (code) => confirmPhoneChange(code),
  },
};

/**
 * Map a failed request/confirm call to donor-facing copy.
 *
 * Backend reality (CHG-2891/CHG-2939 flows; AppException bodies carry the
 * copy in `message` — see the envelope note inside the function):
 *   429                       → per-account (3/day) or per-identifier (hourly)
 *                               rate limit (RateLimitError)
 *   401 (confirm)             → wrong OTP code (OtpService UnauthorizedError)
 *   422 message ~ "expired"   → staged change / code expired
 *   422 other string message  → safe, donor-actionable ValidationError copy
 *                               ("same as current phone", "Too many attempts.
 *                               Request a new code.", "No active verification
 *                               code…") — surfaced as-is
 *   503                       → delivery/SMS provider unavailable. Since
 *                               CHG-2953 (DRIFT-1141 resolved) a failed OTP
 *                               SMS send surfaces here too — no more false
 *                               "code sent", and the 3/day slot is refunded
 *                               server-side so retrying is safe.
 *
 * Exported for direct unit coverage of every branch.
 */
export function describeContactChangeError(
  err: unknown,
  phase: "request" | "confirm",
  kind: ContactChangeKind,
): string {
  const e = err as {
    response?: { status?: number; data?: { detail?: unknown; message?: unknown } };
  };
  const status = e?.response?.status;
  // CHG-2942 fix pass (review issue 1) — AppException errors serialize as
  // {error, message, details, payload} (core/exceptions.py ErrorResponse):
  // the donor-actionable copy lives in `message`, NOT `detail`. Reading
  // only `detail` meant the expired-code 422 and the ValidationError
  // pass-throughs below NEVER rendered (always generic fallback). `detail`
  // is kept as the fallback for FastAPI HTTPException bodies and legacy
  // Pydantic-422 error arrays (the runtime ValidationErrorResponse sets
  // `message` too, mirroring `detail`).
  const data = e?.response?.data;
  const raw = data?.message ?? data?.detail;
  const detailText =
    typeof raw === "string"
      ? raw
      : Array.isArray(raw) && raw.length > 0
        ? String((raw[0] as { msg?: string })?.msg ?? "")
        : "";

  if (status === 429) {
    // tier-05 copy.rate_limit_error
    return "Too many change requests. Try again tomorrow.";
  }
  if (status === 503) {
    return KIND_CONFIG[kind].smsUnavailableCopy;
  }
  if (phase === "confirm") {
    if (status === 401) {
      // tier-05 error copy ("Incorrect code") + recovery hint.
      return "Incorrect code. Check the 6 digits and try again.";
    }
    if (status === 422 && /expired/i.test(detailText)) {
      // tier-05 copy.code_expired
      return "Code expired. Please request a new one.";
    }
  }
  if (status === 422 && detailText) {
    // Remaining 422s are donor-actionable ValidationError copy with no
    // enumeration content ("New phone is the same as current phone.",
    // "Too many attempts. Request a new code.", …).
    return detailText;
  }
  return phase === "request"
    ? "We couldn't start the change. Please try again."
    : "We couldn't verify the code. Please try again.";
}

/**
 * CHG-2942 fix pass (review issue 2) — distinguish the two 401s that can
 * reach the confirm step. client.ts exempts the OTP confirm endpoints from
 * the force-logout interceptor (a WRONG code 401s by design), but that
 * exemption also lets a DEAD-SESSION 401 (expired/revoked token) through to
 * the dialog, which previously rendered "Incorrect code" forever with no
 * way to re-auth. The bodies are distinguishable:
 *
 *   wrong code   → AppException envelope {error: "unauthorized",
 *                  message: "Invalid code"} (core/exceptions.py)
 *   dead session → FastAPI HTTPException {detail: "Not authenticated"}
 *                  (core/deps.py get_current_account) — no error/message keys
 *
 * Any confirm 401 WITHOUT the AppException envelope is treated as a dead
 * session → the dialog calls requestForceLogout() instead of showing the
 * invalid-code copy. Exported for direct unit coverage.
 */
export function isSessionExpired401(err: unknown): boolean {
  const e = err as {
    response?: { status?: number; data?: { error?: unknown; message?: unknown } };
  };
  if (e?.response?.status !== 401) return false;
  const body = e.response?.data;
  return !(typeof body?.error === "string" && typeof body?.message === "string");
}

export interface ContactChangeDialogProps {
  kind: ContactChangeKind;
  open: boolean;
  onClose: () => void;
  /** Fires once after a successful confirm (caller invalidates queries). */
  onSuccess?: () => void;
}

export function ContactChangeDialog({
  kind,
  open,
  onClose,
  onSuccess,
}: ContactChangeDialogProps) {
  const cfg = KIND_CONFIG[kind];
  const [step, setStep] = useState<Step>("enter");
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** The normalized contact value a code was sent to (step ≥ code). */
  const [sentTo, setSentTo] = useState<string>("");

  const reset = useCallback(() => {
    setStep("enter");
    setValues({});
    setError(undefined);
    setFieldErrors({});
    setSentTo("");
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const requestMutation = useMutation({
    mutationFn: (value: string) => cfg.request(value),
    onSuccess: () => {
      setError(undefined);
      setFieldErrors({});
      setStep("code");
    },
    onError: (err: unknown) =>
      setError(describeContactChangeError(err, "request", kind)),
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) => cfg.confirm(code),
    onSuccess: () => {
      setError(undefined);
      setFieldErrors({});
      setStep("done");
      // Invalidate immediately so the page refetch already shows the new
      // verified contact while the success state is on screen.
      onSuccess?.();
    },
    onError: (err: unknown) => {
      // CHG-2942 fix pass (review issue 2) — a 401 without the wrong-code
      // envelope means the SESSION is dead (the interceptor exemption let
      // it through to us); re-auth instead of rendering "Incorrect code".
      if (isSessionExpired401(err)) {
        requestForceLogout();
        return;
      }
      setError(describeContactChangeError(err, "confirm", kind));
    },
  });

  const busy = requestMutation.isPending || confirmMutation.isPending;

  const handleSubmit = useCallback(() => {
    setError(undefined);
    if (step === "enter") {
      const raw = String(values[cfg.enterField.name] ?? "");
      const validationError = cfg.validate(raw);
      if (validationError) {
        setFieldErrors({ [cfg.enterField.name]: validationError });
        return;
      }
      setFieldErrors({});
      const normalized = cfg.normalize(raw);
      setSentTo(normalized);
      requestMutation.mutate(normalized);
      return;
    }
    if (step === "code") {
      const code = String(values.code ?? "");
      if (!/^\d{6}$/.test(code)) {
        setFieldErrors({ code: "Enter the 6-digit code." });
        return;
      }
      setFieldErrors({});
      confirmMutation.mutate(code);
      return;
    }
    // done
    close();
  }, [step, values, cfg, requestMutation, confirmMutation, close]);

  if (!open) return null;

  const fields: FormFieldDef[] =
    step === "enter"
      ? [cfg.enterField]
      : step === "code"
        ? [
            {
              name: "code",
              type: "text",
              label: "Verification code",
              required: true,
              helperText: "The code expires after 10 minutes.",
              // TextField (CHG-2433) supports placeholder/maxLength only —
              // no inputMode prop, so it is deliberately not passed here.
              props: { maxLength: 6, placeholder: "123456" },
            },
          ]
        : [];

  const description =
    step === "enter" ? (
      cfg.enterDescription
    ) : step === "code" ? (
      <span>
        {cfg.codeSentTo(sentTo)}{" "}
        <button
          type="button"
          className="btn btn-ghost"
          style={{ padding: "0 4px", height: "auto", fontSize: 12 }}
          onClick={reset}
          disabled={busy}
        >
          Wrong number?
        </button>
      </span>
    ) : (
      cfg.successCopy
    );

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={cfg.title[step]}
      description={description}
      size="sm"
      fields={fields}
      values={values}
      onChange={(name, value) => setValues((prev) => ({ ...prev, [name]: value }))}
      onSubmit={handleSubmit}
      submitLabel={cfg.submitLabel[step]}
      cancelLabel={step === "done" ? "Close" : "Cancel"}
      busy={busy}
      error={error}
      errors={fieldErrors}
    />
  );
}
